<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FormField from './FormField.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  // Kept for a uniform `*Fields.vue` interface; free-form details have no
  // per-field validation of their own.
  // eslint-disable-next-line vue/no-unused-properties
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.OTHER';

// Seeded once from the initial modelValue; the parent must `:key` this component
// by booking id so switching bookings remounts it.
const rows = ref(
  Object.entries(props.modelValue)
    .filter(([key, value]) => key !== 'notes' && typeof value !== 'object')
    .map(([key, value]) => ({ key, value: String(value) }))
);

const emitRows = () => {
  const details = Object.fromEntries(
    rows.value
      .filter(row => row.key.trim())
      .map(row => [row.key.trim(), row.value])
  );
  emit('update:modelValue', { ...details, notes: props.modelValue.notes });
};

const setRow = (index, patch) => {
  rows.value[index] = { ...rows.value[index], ...patch };
  emitRows();
};

const addRow = () => {
  rows.value.push({ key: '', value: '' });
};

const removeRow = index => {
  rows.value.splice(index, 1);
  emitRows();
};

const setNotes = notes =>
  emit('update:modelValue', { ...props.modelValue, notes });
</script>

<template>
  <div class="flex flex-col gap-4">
    <FormField :label="t(`${KEY}.FIELDS`)">
      <div class="flex flex-col gap-2">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="flex items-center gap-2"
        >
          <Input
            size="sm"
            class="w-40"
            :placeholder="t(`${KEY}.KEY_PLACEHOLDER`)"
            :model-value="row.key"
            @update:model-value="setRow(index, { key: $event })"
          />
          <Input
            size="sm"
            class="flex-1"
            :placeholder="t(`${KEY}.VALUE_PLACEHOLDER`)"
            :model-value="row.value"
            @update:model-value="setRow(index, { value: $event })"
          />
          <Button
            ghost
            xs
            ruby
            icon="i-lucide-x"
            :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.REMOVE')"
            @click="removeRow(index)"
          />
        </div>
        <Button
          ghost
          xs
          icon="i-lucide-plus"
          class="self-start"
          :label="t(`${KEY}.ADD_FIELD`)"
          @click="addRow"
        />
      </div>
    </FormField>

    <TextArea
      :model-value="modelValue.notes"
      :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES')"
      :placeholder="
        t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES_PLACEHOLDER')
      "
      :max-length="2000"
      @update:model-value="setNotes"
    />
  </div>
</template>
